// ===========================================
// Migration review (enquiries plan §6.3)
// ===========================================
// The old "Resolved" enquiries from before the pipeline. Each needs a real outcome picked by a
// person; the server suggests "Enrolled" when the same email already belongs to a family.

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import type { MigrationReviewRow, ReviewOutcome } from '../../../api/enquiry.api';
import { apiErrorMessage, useEnquiryConfig, useMigrationReview, useReviewOutcome } from '../../../hooks/useEnquiries';
import { decodeMessage, formatDay, stageLabel, stageView } from './enquiryUi';

const LONG_MESSAGE = 240;

export interface MigrationReviewDialogProps {
  open: boolean;
  onClose: () => void;
}

function ReviewMessage({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const decoded = decodeMessage(text);
  const long = decoded.length > LONG_MESSAGE;
  const shown = long && !expanded ? `${decoded.slice(0, LONG_MESSAGE)}…` : decoded;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {shown}
      </Typography>
      {long && (
        <Button size="small" onClick={() => setExpanded((v) => !v)} sx={{ px: 0 }}>
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Box>
  );
}

export default function MigrationReviewDialog({ open, onClose }: MigrationReviewDialogProps) {
  const { data: rows, isLoading, isError, error, refetch } = useMigrationReview(open);
  const { data: config } = useEnquiryConfig();
  const outcome = useReviewOutcome();
  const waitlistEnabled = stageView(config, 'WAITLIST')?.enabled !== false;
  // The outcomes are stages: name each the way the school names it (whole-phase review L1-u).
  const label = (stage: ReviewOutcome) => stageLabel(config, stage);
  // Every row with a request in flight (the mutation object only knows the latest one).
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());

  const choose = async (row: MigrationReviewRow, value: ReviewOutcome) => {
    setPendingIds((prev) => new Set(prev).add(row.id));
    try {
      await outcome.mutateAsync({
        id: row.id,
        outcome: value,
        ...(value === 'ENROLLED' && row.suggestedFamilyId ? { familyId: row.suggestedFamilyId } : {}),
      });
    } catch {
      // the hook reports the failure
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Enquiries to review</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These are old &quot;Resolved&quot; enquiries from before the upgrade. Pick what really happened to each one.
        </Typography>

        {isLoading ? (
          <Stack spacing={2} data-testid="review-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={96} />
            ))}
          </Stack>
        ) : isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {apiErrorMessage(error, 'Failed to load the enquiries to review.')}
          </Alert>
        ) : !rows || rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Nothing left to review.
          </Typography>
        ) : (
          <Stack spacing={0} divider={<Divider flexItem />}>
            {rows.map((row) => {
              const busy = pendingIds.has(row.id);
              return (
                <Box key={row.id} sx={{ py: 2, minWidth: 0 }} data-testid={`review-row-${row.id}`}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography fontWeight={600}>
                      {row.firstName} {row.lastName}
                    </Typography>
                    {row.suggestedOutcome === 'ENROLLED' && (
                      <Chip size="small" color="success" label={`Suggested: ${label('ENROLLED')}`} />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {[row.email, row.phone, `Received ${formatDay(row.receivedOn)}`].filter(Boolean).join(' · ')}
                  </Typography>
                  {row.message && <ReviewMessage text={row.message} />}
                  {row.duplicates.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {row.duplicates.map((d) => (
                        <Typography key={d.id} variant="caption" color="text.secondary" display="block">
                          Also enquired: {stageLabel(config, d.stage)} {formatDay(d.receivedOn)}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {row.suggestedFamilyId && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {row.suggestedFamilyName
                        ? `${label('ENROLLED')} links to the ${row.suggestedFamilyName} family.`
                        : `${label('ENROLLED')} links to the family with this email.`}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      variant={row.suggestedOutcome === 'ENROLLED' ? 'contained' : 'outlined'}
                      disabled={busy}
                      onClick={() => void choose(row, 'ENROLLED')}
                    >
                      {label('ENROLLED')}
                    </Button>
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => void choose(row, 'NOT_PROCEEDING')}>
                      {label('NOT_PROCEEDING')}
                    </Button>
                    {waitlistEnabled && (
                      <Button size="small" variant="outlined" disabled={busy} onClick={() => void choose(row, 'WAITLIST')}>
                        {label('WAITLIST')}
                      </Button>
                    )}
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => void choose(row, 'IN_CONVERSATION')}>
                      {label('IN_CONVERSATION')}
                    </Button>
                    <Button size="small" variant="outlined" color="error" sx={{ color: 'error.dark', borderColor: 'error.dark' }} disabled={busy} onClick={() => void choose(row, 'SPAM')}>
                      {label('SPAM')}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
